/**
 * Between a project's execution routes and the four-column route rows the editor shows.
 *
 * Rows for "All machines" are the role's general list (`execution.<role>`); rows naming a
 * machine are that machine's section for the role (`execution.machines.<name>.<role>`), which
 * replaces the general list on that machine. Each role table holds two lists with the same shape: `routes`
 * (standard) and `light_routes` (what a small ticket tries first). Routing reads each of those
 * lists on its own, so the editor's order across them is stored separately, as the general
 * table's `order` (`light_order` for the light list): one machine name, or null, per row. Pure
 * so the round trip can be tested without a DOM.
 */
import { EXECUTION_ROLES, type ExecutionRole, type ExecutionRoleSection, type ExecutionRoute, type ExecutionSection, type ThinkingLevel } from '@nightshift/config';

import type { RouteRow } from './route-list.ts';

/** Which of a role table's two lists an editor is showing. */
export type RouteListKey = 'routes' | 'light_routes';

/** The general table's key that orders one list's rows. */
function orderKey(list: RouteListKey): 'order' | 'light_order' {
  return list === 'routes' ? 'order' : 'light_order';
}

/** The role's rows as stored: the general list first, then each machine section. */
function storedRows(
  execution: Partial<ExecutionSection> | undefined,
  role: ExecutionRole,
  list: RouteListKey,
): RouteRow[] {
  const rows: RouteRow[] = [];
  for (const route of execution?.[role]?.[list] ?? []) rows.push({ machine: null, ...route });
  for (const [name, roles] of Object.entries(execution?.machines ?? {})) {
    for (const route of roles?.[role]?.[list] ?? []) rows.push({ machine: name, ...route });
  }
  return rows;
}

/**
 * The role's rows in display order. A saved order takes each row from the front of the list it
 * names; rows it does not account for, after another surface edited a list, follow in stored order.
 */
export function rowsForRole(
  execution: Partial<ExecutionSection> | undefined,
  role: ExecutionRole,
  list: RouteListKey = 'routes',
): RouteRow[] {
  const rows = storedRows(execution, role, list);
  const order = execution?.[role]?.[orderKey(list)];
  if (!order) return rows;
  const remaining = [...rows];
  const ordered: RouteRow[] = [];
  for (const machine of order) {
    const index = remaining.findIndex((row) => row.machine === machine);
    if (index >= 0) ordered.push(...remaining.splice(index, 1));
  }
  return [...ordered, ...remaining];
}

function copyRole(section: ExecutionRoleSection | undefined): ExecutionRoleSection | undefined {
  if (!section) return undefined;
  const routes = [...(section.routes ?? [])];
  const light = [...(section.light_routes ?? [])];
  if (routes.length === 0 && light.length === 0 && !section.order && !section.light_order) return undefined;
  const copy: ExecutionRoleSection = { routes };
  if (light.length > 0) copy.light_routes = light;
  if (section.order) copy.order = [...section.order];
  if (section.light_order) copy.light_order = [...section.light_order];
  return copy;
}

/** Set one list on a role table, dropping the table when it ends up holding nothing. */
function withList(
  section: ExecutionRoleSection | undefined,
  list: RouteListKey,
  routes: readonly ExecutionRoute[],
): ExecutionRoleSection | undefined {
  return copyRole({ ...section, routes: [...(section?.routes ?? [])], light_routes: [...(section?.light_routes ?? [])], [list]: [...routes] });
}

/** Rebuild one list of one role from rows, keeping every other list and role as they were. */
export function sectionWithRole(
  execution: Partial<ExecutionSection> | undefined,
  role: ExecutionRole,
  rows: readonly RouteRow[],
  list: RouteListKey = 'routes',
): Partial<ExecutionSection> {
  const next: Partial<ExecutionSection> = {};
  if (execution?.implementer_report_version === 1) next.implementer_report_version = 1;
  for (const each of EXECUTION_ROLES) {
    const copied = copyRole(execution?.[each]);
    if (copied) next[each] = copied;
  }
  const machines: NonNullable<ExecutionSection['machines']> = {};
  for (const [name, roles] of Object.entries(execution?.machines ?? {})) {
    for (const each of EXECUTION_ROLES) {
      const copied = copyRole(roles?.[each]);
      if (copied) machines[name] = { ...machines[name], [each]: copied };
    }
  }

  const general: ExecutionRoute[] = [];
  const byMachine = new Map<string, ExecutionRoute[]>();
  for (const row of rows) {
    const route: ExecutionRoute = {
      harness: row.harness as ExecutionRoute['harness'],
      model: row.model,
      thinking: (row.thinking ?? null) as ThinkingLevel | null,
    };
    if (row.machine === null) general.push(route);
    else {
      const name = row.machine.toLowerCase();
      byMachine.set(name, [...(byMachine.get(name) ?? []), route]);
    }
  }

  const roleTable = withList(next[role], list, general);
  if (roleTable) next[role] = roleTable;
  else delete next[role];
  for (const name of new Set([...Object.keys(machines), ...byMachine.keys()])) {
    const table = withList(machines[name]?.[role], list, byMachine.get(name) ?? []);
    const roles = { ...machines[name] };
    if (table) roles[role] = table;
    else delete roles[role];
    if (Object.keys(roles).length > 0) machines[name] = roles;
    else delete machines[name];
  }
  if (Object.keys(machines).length > 0) next.machines = machines;

  // Keep the rows' order when the stored lists alone would show them differently.
  const key = orderKey(list);
  const order = rows.map((row) => row.machine?.toLowerCase() ?? null);
  const table = next[role];
  if (table) delete table[key];
  const shown = rowsForRole(next, role, list).map((row) => row.machine);
  if (order.some((machine, index) => machine !== shown[index])) next[role] = { ...(table ?? { routes: [] }), [key]: order };
  else if (table && !copyRole(table)) delete next[role];
  return next;
}
